import { EnhancedStockData } from '@/types/deals';

export type SortField = 
  | 'stock_name' 
  | 'current_price' 
  | 'market_capitalization' 
  | 'price_to_earning'
  | 'max_activity_days'
  | 'total_traded_percentage';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

// Helper function to get the total traded percentage for a stock
export const getTotalTradedPercentage = (stock: EnhancedStockData): number => {
  if (!stock.other_deals || stock.other_deals.length === 0) return 0;
  return stock.other_deals.reduce((sum, deal) => sum + (deal.traded_percentage || 0), 0);
};

// Helper function to get the maximum of buy or sell days for a stock
export const getMaxActivityDays = (stock: EnhancedStockData): number => {
  if (!stock.clientDealSummaries || stock.clientDealSummaries.length === 0) {
    return 0;
  }

  // Calculate buy and sell days for each client
  const buyDaysMap = new Map<string, Set<string>>();
  const sellDaysMap = new Map<string, Set<string>>();
  
  stock.deals?.forEach(deal => {
    const clientName = deal.client_name;
    const dealDate = new Date(deal.reported_date).toISOString().split('T')[0];
    
    if (deal.action === 'Purchase') {
      if (!buyDaysMap.has(clientName)) {
        buyDaysMap.set(clientName, new Set<string>());
      }
      buyDaysMap.get(clientName)?.add(dealDate);
    } else if (deal.action === 'Sell') {
      if (!sellDaysMap.has(clientName)) {
        sellDaysMap.set(clientName, new Set<string>());
      }
      sellDaysMap.get(clientName)?.add(dealDate);
    }
  });
  
  // Find the maximum number of buy or sell days across all clients
  let maxBuyDays = 0;
  let maxSellDays = 0;
  
  buyDaysMap.forEach(days => {
    maxBuyDays = Math.max(maxBuyDays, days.size);
  });
  
  sellDaysMap.forEach(days => {
    maxSellDays = Math.max(maxSellDays, days.size);
  });
  
  return Math.max(maxBuyDays, maxSellDays);
};

// Sort function for stocks
export const sortStocks = (
  stocks: EnhancedStockData[], 
  sortConfig: SortConfig
): EnhancedStockData[] => {
  return [...stocks].sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;
    
    switch (sortConfig.field) {
      case 'stock_name':
        aValue = a.stock_name.toLowerCase();
        bValue = b.stock_name.toLowerCase();
        break;
      case 'current_price':
        aValue = a.financial_data?.current_price || 0;
        bValue = b.financial_data?.current_price || 0;
        break;
      case 'market_capitalization':
        aValue = a.financial_data?.market_capitalization || 0;
        bValue = b.financial_data?.market_capitalization || 0;
        break;
      case 'price_to_earning':
        aValue = a.financial_data?.price_to_earning || 0;
        bValue = b.financial_data?.price_to_earning || 0;
        break;
      case 'max_activity_days':
        aValue = getMaxActivityDays(a);
        bValue = getMaxActivityDays(b);
        break;
      case 'total_traded_percentage':
        aValue = getTotalTradedPercentage(a);
        bValue = getTotalTradedPercentage(b);
        break;
      default:
        return 0;
    }
    
    if (sortConfig.direction === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
    }
  });
}; 